import { omit, has, groupBy } from 'lodash-es'
import { findEpiSuiteGroupById } from '@/components/combined-predict/models/epiSuite/epiSuiteColumnGroups'
import { getSummaryValue, buildDisplayText, makeFactorTable, makeParameterTable } from '../utils'

const makeTablesFromGroupedQsarClasses = ({ groupedByQsarClass, unit, tableHeaderSecondaryText, selectedEndpoint }) =>
  Object.entries(groupedByQsarClass)?.reduce((acc, [qsarClass, qsarClassResults]) => {
    const tableValues = qsarClassResults
      ?.filter((result) => (selectedEndpoint ? result.endpoint === selectedEndpoint : true))
      .map((result) => ({
        ...omit(result, ['qsarClass', 'flags', 'concentration', ...(selectedEndpoint ? ['endpoint'] : [])]),
        ...(has(result, 'concentration')
          ? {
              [`${selectedEndpoint ? selectedEndpoint : 'concentration'}${unit ? ` (${unit})` : ''}`]: result?.flags
                ?.length
                ? {
                    value: result.concentration,
                    flags: result?.flags,
                  }
                : result.concentration,
            }
          : {}),
      }))

    const table = makeFactorTable(
      tableValues,
      buildDisplayText({
        mainText: qsarClass,
        ...(tableHeaderSecondaryText ? { secondaryText: tableHeaderSecondaryText } : {}),
      }),
    )

    if (table) acc.push(table)
    return acc
  }, [])

const summaryMapper = (item, meta) => {
  const groupedByQsarClass = groupBy(item?.modelResults, 'qsarClass')

  const makeSummaryValue = (selectedOrganism) =>
    getSummaryValue(
      {
        value: selectedOrganism?.concentration,
        unit: meta?.unit,
        belowUnit: selectedOrganism?.qsarClass,
      },
      `Estimated ${selectedOrganism?.duration} ${selectedOrganism?.endpoint} for ${selectedOrganism?.organism}`,
    )

  const selectedOrganismName = 'Fish'
  const selectedEndpoint = 'LC50'

  const summaryValues = Object.entries(groupedByQsarClass)?.reduce((acc, [qsarClass, value]) => {
    const selectedOrganism = value?.find(
      (result) => result.organism === selectedOrganismName && result.endpoint === selectedEndpoint,
    )
    if (selectedOrganism) acc.push(makeSummaryValue(selectedOrganism))
    return acc
  }, [])

  const lc50Tables = makeTablesFromGroupedQsarClasses({
    groupedByQsarClass,
    tableHeaderSecondaryText: 'Acute Toxicity',
    selectedEndpoint,
    unit: meta?.unit,
  })

  return {
    values: summaryValues,
    tables: lc50Tables,
  }
}

const descriptorsMapper = (item, meta) => {
  const parameterTable = makeParameterTable(item?.parameters)

  const groupedByQsarClass = groupBy(item?.modelResults, 'qsarClass')

  const modelResultsTables = makeTablesFromGroupedQsarClasses({ groupedByQsarClass, unit: meta?.unit })

  return { tables: [parameterTable, ...modelResultsTables] }
}

const ecosar = (items) => {
  const item = items?.find((item) => item?.property === 'ecosar')
  const meta = findEpiSuiteGroupById('ecosar')

  return {
    title: meta?.name,
    description: 'Ecological Structure Activity Relationships',
    helpPage: meta?.helpPage,
    summary: summaryMapper(item, meta),
    modelDescriptors: descriptorsMapper(item, meta),
    modelOutput: item?.output ?? null,
  }
}

export { ecosar }
